import { useNavigate } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Trophy, Clock, type LucideIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { EstadoConexionBadge } from './EstadoConexionBadge'
import { kpiIconClasses } from '../lib/reporteColor'
import type { ConfiguracionFidelizacion } from '../types'

function formatUltimoUso(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-EC', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Guayaquil',
    })
  } catch {
    return iso
  }
}

interface Props {
  config: ConfiguracionFidelizacion | null | undefined
  programaNombre?: string | null
  icon?: LucideIcon
  /** Sub-título debajo del nombre (ej. "Resumen", "Registros", "Ventas", "Canjes"). */
  seccion?: string
  onRefresh: () => void
  refreshing?: boolean
}

export function ReporteHeader({
  config,
  programaNombre,
  icon: Icon = Trophy,
  seccion,
  onRefresh,
  refreshing,
}: Props) {
  const navigate = useNavigate()
  const colorNombre = config?.color_nombre || 'emerald'
  const colorTono = config?.color_tono || '500'
  const c = kpiIconClasses(colorNombre, colorTono)

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div className="flex items-center gap-3 min-w-0">
        <Button variant="ghost" size="sm" onClick={() => navigate('/reportes-fidelizacion')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className={`flex h-10 w-10 items-center justify-center rounded-md ${c.wrap}`}>
          <Icon className={`h-5 w-5 ${c.icon}`} />
        </div>
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight truncate">
            {config?.nombre ?? programaNombre ?? 'Reporte'}
          </h1>
          {seccion && (
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{seccion}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {config?.ultimo_uso && (
          <div className="hidden h-8 items-center gap-2 rounded-md border border-gray-300 px-3 text-xs dark:border-gray-700 sm:flex">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="text-right leading-tight">
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground">
                Última actualización
              </p>
              <p className="font-medium text-black dark:text-white">
                {formatUltimoUso(config.ultimo_uso)}
              </p>
            </div>
          </div>
        )}
        {config && (
          <EstadoConexionBadge
            ultimoUso={config.ultimo_uso}
            ultimoError={config.ultimo_error}
            activo={config.activo}
          />
        )}
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}>
          <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refrescar
        </Button>
      </div>
    </div>
  )
}
